import React, { useState, useEffect } from 'react';
import { ChefHat, Leaf, Sparkles, Users } from 'lucide-react';
import { getAboutContent } from '../services/api';

const DEFAULT_ABOUT = {
  title: 'Tentang Kami',
  description: 'Rumah Makan Aroma Cempaka telah menjadi destinasi kuliner favorit di Kota Jambi untuk pecinta masakan Padang autentik. Dengan pengalaman bertahun-tahun, kami berkomitmen menyajikan hidangan dengan cita rasa khas Minangkabau yang terjaga keasliannya.',
  highlights: [
    { title: 'Rasa Autentik', description: 'Resep turun temurun dari tanah Minang' },
    { title: 'Bahan Berkualitas', description: 'Hanya menggunakan bahan segar pilihan' },
    { title: 'Kebersihan Terjamin', description: 'Standar kebersihan tinggi di setiap proses' },
    { title: 'Pelayanan Prima', description: 'Tim yang ramah dan profesional' }
  ]
};

const iconMap = [ChefHat, Leaf, Sparkles, Users];

const AboutSection = () => {
  const [about, setAbout] = useState(DEFAULT_ABOUT);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getAboutContent();
      if (data) {
        setAbout({ ...DEFAULT_ABOUT, ...data });
      }
    };
    fetchData();
  }, []);

  const highlights = Array.isArray(about.highlights) ? about.highlights : DEFAULT_ABOUT.highlights;

  return (
    <section id="tentang" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-amber-50 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-red-50 rounded-full translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <img
                    src="https://images.unsplash.com/photo-1688084442478-3b499d17126f"
                    alt="Rendang"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <img
                    src="https://images.pexels.com/photos/35173901/pexels-photo-35173901.jpeg"
                    alt="Proses Memasak"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <img
                    src="https://images.unsplash.com/photo-1677921755291-c39158477b8e"
                    alt="Nasi Padang"
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <img
                    src="https://images.pexels.com/photos/3008740/pexels-photo-3008740.jpeg"
                    alt="Hidangan Padang"
                    className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 to-red-600 text-white px-8 py-4 rounded-2xl shadow-xl">
              <div className="text-center">
                <span className="block text-3xl font-bold">10+</span>
                <span className="text-sm opacity-90">Tahun Pengalaman</span>
              </div>
            </div>
          </div>

          <div>
            <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium mb-4">
              Tentang Kami
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Cita Rasa Autentik dari Tanah Minang
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              {about.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => {
                const Icon = iconMap[index] || ChefHat;
                return (
                  <div
                    key={index}
                    className="group p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl hover:from-amber-100 hover:to-orange-100 transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-red-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">{highlight.title}</h4>
                    <p className="text-gray-600 text-sm">{highlight.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
